import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { buildLegacyUrinalysis } from "features/pregnancy/utils/buildLegacyUrinalysis";
import { showMutationError } from "utils/showMutationError";

const saveAnteNatalConsultation = async ({
  values,
  tenantVal,
  patientId,
  pregnancyId,
  consultationId,
  consultationDate,
}) => {
  const systolic = values.bloodPressureSystolic
    ? parseInt(values.bloodPressureSystolic, 10)
    : null;
  const diastolic = values.bloodPressureDiastolic
    ? parseInt(values.bloodPressureDiastolic, 10)
    : null;
  const legacyBp = systolic && diastolic ? `${systolic}/${diastolic}` : undefined;
  const fundalHeight = values.fundalHeightCm
    ? parseInt(values.fundalHeightCm, 10)
    : null;

  const axiosInstance = createAxiosInstance();

  // TODO (KIN-BE-MNCMS): Fields below are sent correctly but NOT persisted by the backend.
  // BE migration adding new columns to antenatal_consultation table has not been applied.
  // All fields from gestationWeeks down to the urinalysis grades are affected.
  // Remove this comment once Eleanor confirms the migration is deployed.
  const response = await axiosInstance.post(`${tenantVal}/antenatalconsultation`, {
    tenant: tenantVal,
    patientId,
    pregnancyId,
    ...(consultationId ? { id: consultationId } : {}),
    // Preserve the existing consultation date on update — the BE does a
    // full-row UPDATE and would otherwise null out the not-null column.
    ...(consultationDate ? { consultationDate } : {}),

    gestationWeeks: values.gestationWeeks ? parseInt(values.gestationWeeks, 10) : null,
    gestationDays: values.gestationDays ? parseInt(values.gestationDays, 10) : null,

    bloodPressureSystolic: systolic,
    bloodPressureDiastolic: diastolic,
    bloodPressure: legacyBp,

    maternalWeightKg: values.maternalWeightKg ? parseFloat(values.maternalWeightKg) : null,
    maternalHeightCm: values.maternalHeightCm ? parseInt(values.maternalHeightCm, 10) : null,

    fundalHeightCm: fundalHeight,
    symphysis: fundalHeight,


    urinalysisGlucose: values.urinalysisGlucose || null,
    urinalysisProtein: values.urinalysisProtein || null,
    urinalysisBlood: values.urinalysisBlood || null,
    urinalysisKetones: values.urinalysisKetones || null,
    urinalysisLeucocytes: values.urinalysisLeucocytes || null,
    // Blank stays null rather than becoming NEGATIVE, so re-saving an old visit that never
    // recorded nitrites does not start claiming a negative result for it.
    urinalysisNitrites: values.urinalysisNitrites || null,
    urinalysis: buildLegacyUrinalysis(values),

    ankleOedema: values.ankleOedema || null,
    uterineContractions: values.uterineContractions || null,

    bmi: values.bmi ? parseFloat(values.bmi) : null,
    haemoglobin: values.haemoglobin ? parseFloat(values.haemoglobin) : null,
    dateOfNextVisitWeeks: values.dateOfNextVisitWeeks
      ? parseInt(values.dateOfNextVisitWeeks, 10)
      : null,

    impression: values.impression || null,
    plan: values.plan || null,
    additionalInfo: values.additionalInfo || null,
  });

  return response?.data?.data ?? response?.data;
};

export const useSaveAnteNatalConsultation = ({
  tenantVal,
  patientId,
  pregnancyId,
  consultationId,
  consultationDate,
  onSuccess,
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: (values) =>
      saveAnteNatalConsultation({
        values,
        tenantVal,
        patientId,
        pregnancyId,
        consultationId,
        consultationDate,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["anteNatalConsultations"] });
      onSuccess?.(data);
    },
    onError: (error) => showMutationError(error),
  });
};
